<script setup>
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  contacts: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const router = useRouter();

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatPhone = phone => {
  if (!phone) return '-';
  return phone;
};

const getInitials = name => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

const openContact = contact => {
  router.push({
    name: 'contacts_edit',
    params: { contactId: contact.id },
  });
};
</script>

<template>
  <div
    class="overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
  >
    <table class="w-full">
      <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
        <tr>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-user size-3.5 opacity-40" />
              <span>{{ t('CONTACTS_LAYOUT.TABLE.NAME') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-mail size-3.5 opacity-40" />
              <span>{{ t('CONTACTS_LAYOUT.TABLE.EMAIL') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-phone size-3.5 opacity-40" />
              <span>{{ t('CONTACTS_LAYOUT.TABLE.PHONE') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-building size-3.5 opacity-40" />
              <span>{{ t('CONTACTS_LAYOUT.TABLE.COMPANY') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-calendar size-3.5 opacity-40" />
              <span>{{ t('CONTACTS_LAYOUT.TABLE.LAST_ACTIVITY') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11"
          >
            <div class="flex items-center justify-end gap-1.5">
              <span>{{ t('CONTACTS_LAYOUT.TABLE.ACTIONS') }}</span>
              <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="bg-n-solid-1">
        <tr
          v-for="contact in contacts"
          :key="contact.id"
          class="transition-all duration-150 ease-out border-t border-n-weak/40 hover:bg-n-blue-2/50 group cursor-pointer"
          @click="openContact(contact)"
        >
          <td class="px-5 py-3">
            <div class="flex items-center gap-3">
              <div
                v-if="contact.thumbnail"
                class="w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
              >
                <img
                  :src="contact.thumbnail"
                  :alt="contact.name"
                  class="w-full h-full object-cover"
                />
              </div>
              <div
                v-else
                class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-n-alpha-3 text-n-slate-11 font-medium text-sm"
              >
                {{ getInitials(contact.name) }}
              </div>
              <div class="min-w-0">
                <span class="text-sm font-medium text-n-slate-12 block">
                  {{ contact.name || '-' }}
                </span>
                <span
                  v-if="contact.availabilityStatus"
                  class="text-xs text-n-slate-10"
                >
                  {{
                    contact.availabilityStatus === 'online'
                      ? t('CONTACTS_LAYOUT.TABLE.ONLINE')
                      : t('CONTACTS_LAYOUT.TABLE.OFFLINE')
                  }}
                </span>
              </div>
            </div>
          </td>
          <td class="px-5 py-3 text-sm text-n-slate-10">
            {{ contact.email || '-' }}
          </td>
          <td class="px-5 py-3 text-sm text-n-slate-10">
            {{ formatPhone(contact.phoneNumber) }}
          </td>
          <td class="px-5 py-3 text-sm text-n-slate-10">
            {{ contact.companyName || contact.company_name || '-' }}
          </td>
          <td class="px-5 py-3 text-sm text-n-slate-10">
            {{ formatDate(contact.lastActivityAt || contact.last_activity_at) }}
          </td>
          <td class="px-5 py-3">
            <div class="flex justify-end gap-2">
              <Button
                icon="i-lucide-eye"
                variant="ghost"
                size="sm"
                color="slate"
                class="!w-8 !h-8"
                :title="t('CONTACTS_LAYOUT.TABLE.VIEW')"
                @click.stop="openContact(contact)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
